import { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Flex, ProgressIndicator, StepItem, StepNavigation, Tag, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Sizes, Colors } from "@jsq/ds/styles";
import { globalEnv } from "~/global-env";
import { type GetComplianceCaseQuery } from "~/graphql/supergraphTypes";
import { ComplianceDocumentType } from "~/pages/Compliance/CaseDetailsDrawer/Documents/DocumentManagement/DocumentManagementList/types";
import { useDocumentRefreshFeatureFlag } from "~/pages/Compliance/flags";
import {
  documentListContainer,
  stepButton,
  expiredBadge,
  stepItemContainer,
  progressIndicator,
  warningIcon,
} from "./styles_vanilla.css";
import { isDocumentExpiredInList } from "./utils";

interface Props {
  complianceCase: GetComplianceCaseQuery["complianceCase"] | undefined;
  documents: ComplianceDocumentType[];
}

const getProgress = (d: ComplianceDocumentType) => {
  return (
    (d.profileId ? 33 : 0) +
    (d.documentPurpose ? 33 : 0) +
    (d.status && d.status !== "new" ? 34 : 0)
  );
};

const DocumentManagementList = ({ complianceCase, documents }: Props) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const history = useHistory();
  const isDocumentRefreshEnabled = useDocumentRefreshFeatureFlag();

  const isEscalationRoute = useMemo(() => {
    return location.pathname.startsWith(`${globalEnv.complianceRoute}/escalations`);
  }, [location.pathname]);

  const navigateToDocument = (documentId: number) => {
    const baseRoute = isEscalationRoute ? "escalations" : "case";
    history.push(
      `${globalEnv.complianceRoute}/${baseRoute}/${complianceCase?.id}/document-management?documentId=${documentId}`,
    );
  };

  const firstDocument = documents[0];
  const documentIdStr = searchParams.get("documentId");
  const documentId = documentIdStr ? parseInt(documentIdStr) : firstDocument.id;

  return (
    <StepNavigation title="Documents" className={documentListContainer}>
      <Flex flexDirection="column">
        {documents.map((doc) => {
          const isExpired = isDocumentRefreshEnabled && isDocumentExpiredInList(doc);
          const progress = getProgress(doc);
          return (
            <StepItem
              key={doc.s3Link}
              onClick={() => navigateToDocument(doc.id)}
              selected={doc.id === documentId}
            >
              <Flex
                justifyContent="space-between"
                alignItems="center"
                gap={Sizes.SM}
                className={stepItemContainer}
              >
                {isExpired ? (
                  <Icon
                    type="exclamation-triangle"
                    size="small"
                    color={Colors.Neutral700}
                    variant="outline"
                    className={warningIcon}
                  />
                ) : progress === 0 ? (
                  <Icon
                    type="circle"
                    size="small"
                    color={Colors.Neutral400}
                    variant="outline"
                    className={progressIndicator}
                  />
                ) : (
                  <ProgressIndicator progress={progress} variant="neutral" />
                )}
                <Text className={stepButton}>{doc.name}</Text>
                {isExpired && (
                  <Tag variant="warning" className={expiredBadge}>
                    Expired
                  </Tag>
                )}
              </Flex>
            </StepItem>
          );
        })}
      </Flex>
    </StepNavigation>
  );
};

export default DocumentManagementList;
